import React, { useState, useEffect, useCallback } from 'react';
import { FaSpinner, FaExpandArrowsAlt, FaTimes, FaChevronLeft, FaChevronRight } from 'react-icons/fa';

interface ImageGalleryProps {
  images: Record<string, any>[];
  fullImageKey?: string;
  thumbnailImageKey?: string;
  altKey?: string;
  backgroundColor?: string;
}

const PLACEHOLDER_IMAGE = (width: number, height: number, text: string = "Image Not Found") => {
  return `https://placehold.co/${width}x${height}/e2e8f0/94a3b8?text=${encodeURIComponent(text)}`;
};

export const ImageGallery = ({
  images,
  fullImageKey = 'full',
  thumbnailImageKey = 'thumbnail',
  altKey = 'alt',
  backgroundColor = 'bg-gray-200',
}: ImageGalleryProps) => {
  const [selectedImageIndex, setSelectedImageIndex] = useState<number>(0);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [mainImageLoaded, setMainImageLoaded] = useState<boolean>(false);

  const handleThumbnailClick = (index: number) => {
    setSelectedImageIndex(index);
    setMainImageLoaded(false);
  };

  const openModal = () => {
    if (images && images.length > 0) {
      setIsModalOpen(true);
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const goToPrevious = useCallback((e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    setSelectedImageIndex((prevIndex) => (prevIndex - 1 + images.length) % images.length);
    setMainImageLoaded(false);
  }, [images?.length]);

  const goToNext = useCallback((e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    setSelectedImageIndex((prevIndex) => (prevIndex + 1) % images.length);
    setMainImageLoaded(false);
  }, [images?.length]);


  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    if (!images || images.length === 0) return;

    if (isModalOpen) {
      if (event.key === 'Escape') {
        closeModal();
      } else if (event.key === 'ArrowRight') {
        goToNext();
      } else if (event.key === 'ArrowLeft') {
        goToPrevious();
      }
    } else { // Handle arrow keys for non-modal view as well
        if (document.activeElement && ['INPUT', 'TEXTAREA', 'SELECT'].includes(document.activeElement.tagName)) {
            return; // Don't interfere with form inputs
        }
        if (event.key === 'ArrowRight') {
            goToNext();
        } else if (event.key === 'ArrowLeft') {
            goToPrevious();
        }
    }
  }, [isModalOpen, images?.length, goToNext, goToPrevious]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);

  if (!images || images.length === 0) {
    return (
      <div className="flex items-center justify-center h-64 bg-gray-100 rounded-lg text-gray-500 p-4">
        No images to display.
      </div>
    );
  }

  const selectedImage = images[selectedImageIndex];
  const nonModalMainImageUrl = (selectedImage?.[thumbnailImageKey] || selectedImage?.[fullImageKey]) as string | undefined;
  const modalFullImageUrl = selectedImage?.[fullImageKey] as string | undefined;
  const altText = selectedImage?.[altKey] as string | undefined;

  return (
    <div className={"w-full max-w-3xl mx-auto font-sans p-4 rounded-lg " + ` ${backgroundColor}`}>
      <div className="relative mb-4 rounded-lg aspect-[4/3] flex items-center justify-center ">
        {selectedImage && nonModalMainImageUrl && (
          <>
            <img
              key={nonModalMainImageUrl + selectedImageIndex + '-main'}
              src={nonModalMainImageUrl}
              alt={altText || `Gallery image ${selectedImageIndex + 1}`}
              className={`object-contain w-full h-full rounded-lg transition-opacity  mb-4 duration-300 ${mainImageLoaded ? 'opacity-100' : 'opacity-0'}`}
              onLoad={() => setMainImageLoaded(true)}
              onError={(e) => {
                (e.target as HTMLImageElement).src = PLACEHOLDER_IMAGE(800, 600, altText || `Image ${selectedImageIndex + 1} Not Found`);
                setMainImageLoaded(true);
              }}
            />
            {!mainImageLoaded && (
              <div className="absolute inset-0 flex items-center justify-center">
                <FaSpinner className="animate-spin h-10 w-10 text-blue-500" />
              </div>
            )}
          </>
        )}
        {images.length > 1 && (
            <>
                <button
                    onClick={goToPrevious}
                    aria-label="Previous image"
                    className="absolute left-2 sm:left-3 top-1/2 -translate-y-1/2 z-10 p-2 bg-black bg-opacity-40 text-white rounded-full hover:bg-opacity-60 focus:outline-none focus:ring-2 focus:ring-white focus:ring-opacity-50 transition-all cursor-pointer"
                >
                    <FaChevronLeft className="w-5 h-5 sm:w-6 sm:h-6" />
                </button>
                <button
                    onClick={goToNext}
                    aria-label="Next image"
                    className="absolute right-2 sm:right-3 top-1/2 -translate-y-1/2 z-10 p-2 bg-black bg-opacity-40 text-white rounded-full hover:bg-opacity-60 focus:outline-none focus:ring-2 focus:ring-white focus:ring-opacity-50 transition-all cursor-pointer"
                >
                    <FaChevronRight className="w-5 h-5 sm:w-6 sm:h-6" />
                </button>
            </>
        )}
        <button
          onClick={openModal}
          aria-label="View full-size image"
          className="absolute top-3 right-3 p-2 bg-black bg-opacity-50 text-white rounded-full hover:bg-opacity-75 focus:outline-none focus:ring-2 focus:ring-white focus:ring-opacity-50 transition-colors cursor-pointer"
        >
          <FaExpandArrowsAlt className="w-6 h-6" />
        </button>
      </div>

      <div className="flex space-x-2 overflow-x-auto pb-2">
        {images.map((image, index) => {
          const thumbSrc = image[thumbnailImageKey] || image[fullImageKey];
          const thumbAlt = image[altKey] || `Thumbnail ${index + 1}`;
          if (!thumbSrc) return null;
          return (
            <button
              key={index + '-thumb'}
              onClick={() => handleThumbnailClick(index)}
              aria-label={`View image ${index + 1}`}
              className={`flex-shrink-0 w-20 h-20 md:w-24 md:h-24 rounded-md overflow-hidden border-2 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-150 cursor-pointer
                           ${index === selectedImageIndex ? 'border-blue-500 scale-105' : 'border-transparent hover:border-gray-400'}`}
            >
              <img
                src={thumbSrc as string}
                alt={thumbAlt as string}
                className="w-full h-full object-cover"
                onError={(e) => {
                  (e.target as HTMLImageElement).src = PLACEHOLDER_IMAGE(100, 100, `Thumb ${index + 1}`);
                }}
              />
            </button>
          );
        })}
      </div>

      {isModalOpen && selectedImage && modalFullImageUrl && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-80 backdrop-blur-sm p-4"
          onClick={closeModal}
          role="dialog"
          aria-modal="true"
          aria-labelledby="modal-title"
        >
          <div
            className="relative bg-white p-2 rounded-lg  max-w-full max-h-full overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center p-2 absolute top-0 left-0 right-0 z-20">
                <h2 id="modal-title" className="sr-only">Full-size image view: {altText || `Image ${selectedImageIndex + 1}`}</h2>
                {/* Placeholder for title or image count if needed */}
                <span className="text-white bg-black bg-opacity-50 px-2 py-1 rounded-md text-sm">
                    {selectedImageIndex + 1} / {images.length}
                </span>
                <button
                onClick={closeModal}
                aria-label="Close full-size image view"
                className="p-2 bg-black bg-opacity-40 text-white rounded-full hover:bg-opacity-60 focus:outline-none focus:ring-2 focus:ring-white focus:ring-opacity-50 transition-colors cursor-pointer"
                >
                <FaTimes className="w-6 h-6" />
                </button>
            </div>


            <div className="relative flex items-center justify-center flex-grow w-full h-full pt-12 pb-4 px-4 sm:px-10">
                {images.length > 1 && (
                    <button
                        onClick={goToPrevious}
                        aria-label="Previous image"
                        className="absolute left-1 sm:left-2 top-1/2 -translate-y-1/2 z-20 p-2 bg-black bg-opacity-30 text-white rounded-full hover:bg-opacity-50 focus:outline-none focus:ring-2 focus:ring-white transition-opacity cursor-pointer"
                    >
                        <FaChevronLeft className="w-6 h-6 sm:w-8 sm:h-8" />
                    </button>
                )}

              <img
                key={modalFullImageUrl + selectedImageIndex + '-modal'}
                src={modalFullImageUrl}
                alt={altText || `Full-size image ${selectedImageIndex + 1}`}
                className="object-contain max-w-[calc(100vw-6rem)] max-h-[calc(100vh-6rem)] sm:max-w-[calc(100vw-8rem)] sm:max-h-[calc(100vh-8rem)] rounded-md"
                onError={(e) => {
                  (e.target as HTMLImageElement).src = PLACEHOLDER_IMAGE(1200, 800, altText || `Image ${selectedImageIndex + 1} Not Found`);
                }}
              />

                {images.length > 1 && (
                    <button
                        onClick={goToNext}
                        aria-label="Next image"
                        className="absolute right-1 sm:right-2 top-1/2 -translate-y-1/2 z-20 p-2 bg-black bg-opacity-30 text-white rounded-full hover:bg-opacity-50 focus:outline-none focus:ring-2 focus:ring-white transition-opacity cursor-pointer"
                    >
                        <FaChevronRight className="w-6 h-6 sm:w-8 sm:h-8" />
                    </button>
                )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Example Usage (can be in a different file like App.tsx)
//
// const App = () => {
//   const sampleImages = [
//     {
//       full: 'https://images.unsplash.com/photo-1586348943529-beaae6c28db9?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80',
//       thumbnail: 'https://images.unsplash.com/photo-1586348943529-beaae6c28db9?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=200&q=80',
//       alt: 'A beautiful landscape with mountains and a lake.',
//     },
//     {
//       full: 'https://images.unsplash.com/photo-1518020382113-a7e8fc38eac9?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80',
//       thumbnail: 'https://images.unsplash.com/photo-1518020382113-a7e8fc38eac9?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=200&q=80',
//       alt: 'A cute pug dog wearing a costume.',
//     },
//     {
//       full: 'https://images.unsplash.com/photo-1470071459604-3b5ec3a7fe05?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80',
//       // No thumbnail for this one, will use full image for thumbnail display
//       alt: 'A serene forest path with sunlight filtering through trees.',
//     },
//     {
//       // This image will intentionally fail to load to show placeholder
//       full: 'https://invalid.url/image.jpg',
//       thumbnail: 'https://invalid.url/thumbnail.jpg',
//       alt: 'An image that will not load',
//     },
//     {
//       full: 'https://images.unsplash.com/photo-1543946207-f0ae5d39cb7u?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80',
//       thumbnail: 'https://images.unsplash.com/photo-1543946207-f0ae5d39cb7u?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=200&q=80',
//       alt: 'City street at night with light trails.',
//     }
//   ];
//
//   return (
//     <div className="p-0 sm:p-4 bg-slate-200 min-h-screen flex items-center justify-center">
//       <ImageGallery images={sampleImages} />
//     </div>
//   );
// };
//
// export default App; // Assuming this is the main App component
